import { ChevronsUpDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DROPDOWN_PANEL_CLASS } from '@/styles/floatingPanel';
import { joinMultiFilterValues, splitMultiFilterValue } from '@/utils/filters';
import type { FilterField } from './advancedFilter.types';
import { FilterPopoverTrigger } from './FilterPopoverTrigger';

const MULTI_SELECT_PANEL_CLASS = `w-max min-w-(--anchor-width) max-w-[min(28rem,calc(100vw-2rem))] gap-0 ${DROPDOWN_PANEL_CLASS}`;
const MULTI_SELECT_LIST_CLASS = 'flex max-h-64 flex-col gap-0.5 overflow-y-auto';
const MULTI_SELECT_OPTION_CLASS = [
  'flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm',
  'transition-colors duration-150 hover:bg-primary/8 motion-reduce:transition-none',
  'has-data-checked:font-medium has-data-checked:text-primary',
].join(' ');
const SUMMARY_LABEL_LIMIT = 2;
const NO_OPTIONS_TEXT = 'Nothing to pick';

interface FilterMultiSelectFieldProps {
  field: FilterField;
  value: string;
  onChange: (value: string) => void;
}

export function FilterMultiSelectField({ field, value, onChange }: FilterMultiSelectFieldProps) {
  const options = field.options ?? [];
  const selectedValues = splitMultiFilterValue(value);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);
  const hasSelection = selectedValues.length > 0;

  const summary =
    selectedLabels.length > 0 && selectedLabels.length <= SUMMARY_LABEL_LIMIT
      ? selectedLabels.join(', ')
      : `${selectedValues.length} selected`;

  function toggle(optionValue: string) {
    const nextValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((selectedValue) => selectedValue !== optionValue)
      : [...selectedValues, optionValue];
    onChange(joinMultiFilterValues(nextValues));
  }

  return (
    <Popover>
      <PopoverTrigger
        disabled={field.disabled}
        render={
          <FilterPopoverTrigger
            icon={ChevronsUpDown}
            text={hasSelection ? summary : (field.placeholder ?? 'All')}
            hasValue={hasSelection}
            clearLabel={`Clear ${field.label}`}
            onClear={() => onChange('')}
          />
        }
      />
      <PopoverContent align="start" className={MULTI_SELECT_PANEL_CLASS}>
        <div className={MULTI_SELECT_LIST_CLASS}>
          {options.length === 0 ? (
            <p className="px-2.5 py-3 text-sm text-muted-foreground">{NO_OPTIONS_TEXT}</p>
          ) : (
            options.map((option) => (
              <label key={option.value} className={MULTI_SELECT_OPTION_CLASS}>
                <Checkbox
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={() => toggle(option.value)}
                  className="mt-0.5 shrink-0"
                />
                <span className="flex min-w-0 flex-col gap-0.5 wrap-break-word">
                  <span>{option.label}</span>
                  {option.description ? (
                    <span className="text-xs font-normal text-muted-foreground">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
